import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * useNavigationGuard — Protection contre la navigation avec modifications non sauvees.
 * Compatible avec BrowserRouter (pas besoin de createBrowserRouter).
 * 
 * Couvre 3 scenarios:
 * 1. beforeunload — refresh navigateur, fermeture onglet, navigation externe
 * 2. popstate — boutons back/forward du navigateur
 * 3. click capture — clics sur liens internes (SPA navigation via <Link>/<NavLink>)
 * 
 * @param {boolean} isDirty - true si le formulaire contient des modifications non sauvees
 * @returns {{ blocker: {state, proceed, reset} }} - interface compatible avec NavigationBlockerDialog
 */
const useNavigationGuard = (isDirty) => {
  const [blocked, setBlocked] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // 1. beforeunload — refresh / close / external navigation
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // 2. popstate — back/forward browser buttons
  useEffect(() => {
    if (!isDirty) return;
    // Push a guard state entry
    window.history.pushState({ __navguard: true }, '', window.location.href);

    const onPopState = () => {
      // Re-push guard state to prevent leaving
      window.history.pushState({ __navguard: true }, '', window.location.href);
      setPendingPath('__back__');
      setBlocked(true);
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isDirty]);

  // 3. click capture — intercept SPA link clicks
  useEffect(() => {
    if (!isDirty) return;

    const handleClick = (e) => {
      const link = e.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || !href.startsWith('/')) return; // Only internal links
      if (href === location.pathname) return; // Same page, no guard needed
      if (link.target === '_blank') return; // External tab, let it go

      e.preventDefault();
      e.stopPropagation();
      setPendingPath(href);
      setBlocked(true);
    };

    document.addEventListener('click', handleClick, true); // Capture phase
    return () => document.removeEventListener('click', handleClick, true);
  }, [isDirty, location.pathname]);

  // Proceed — user confirmed leaving
  const proceed = useCallback(() => {
    setBlocked(false);
    const path = pendingPath;
    setPendingPath(null);
    if (path === '__back__') {
      // Go back past the guard state (2 entries: the guard push + the original)
      window.history.go(-2);
    } else if (path) {
      navigate(path);
    }
  }, [pendingPath, navigate]);

  // Reset — user cancelled, stay on page
  const reset = useCallback(() => {
    setBlocked(false);
    setPendingPath(null);
  }, []);

  // Blocker interface compatible with NavigationBlockerDialog
  const blocker = {
    state: blocked ? 'blocked' : 'unblocked',
    proceed,
    reset,
  };

  return { blocker };
};

export default useNavigationGuard;
